import {
	DateString,
	Dialog,
	DropOverActionList,
	FormattedText,
	IconButton,
	TableView,
	Toolbar,
	renderReactDialog,
	useConfirmationDialog
} from "client/Components";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useUnmountedCancellation,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	DocumentItemVersion,
	get,
	download as downloadVersion,
	restore,
	deleteVersion
} from "client/Api/Documents/SharePoint/sharePointVersion";
import {
	DocumentOpenPossibilities,
	calculate
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import {
	FileViewModel,
	ListLocationViewModel,
	Permissions
} from "client/Api/Documents/SharePoint/common";
import {
	Action,
	ActionKind,
	reduceToAvailableActions
} from "client_legacy/ndaw/actions";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { isWindows } from "client_legacy/ndaw/mobile";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./VersionDialog.module.less";
import * as openInLocalAppAction from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { DocumentForOpening } from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { downloadDocument } from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { TimeZone } from "client/DateAndTime/timezones";

export type VersionDialogProps = {
	item: FileViewModel;
	onClose: () => void;
};

export const VersionDialog = (props: VersionDialogProps): ReactElement => {
	const {
		item,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [versions, setVersions] = useState<readonly DocumentItemVersion[]>([]);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [reload, loadVersion] = useForceRerender();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	const openHandler = useCallback(
		(version: DocumentItemVersion): void => {
			const documentToOpen: DocumentForOpening = {
				name: item.name,
				itemType: item.itemType,
				location: item.location,
				serverRelativeUrl: version.path
			};

			openInLocalAppAction.open(documentToOpen);
		},
		[
			item.itemType,
			item.location,
			item.name
		]
	);

	const downloadHandler = useCallback(
		(version: DocumentItemVersion): void => {
			if (version.id === null) {
				downloadDocument(item);
			} else {
				downloadVersion({
					fileId: item.id,
					location: item.location,
					id: version.id
				});
			}
		},
		[item]
	);

	const restoreHandler = useCallback(
		async (version: DocumentItemVersion): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: (
					<FormattedText
						text={localize("_AreYouSureYouWantToRestoreFullName")}
						thingsToReplace={[
							{
								what: "{FullName}",
								filler: `${localize("_Version")} ${version.label}`
							}
						]}
					/>
				),
				children: localize("_ThisVersionWillReplaceTheCurrentVersion"),
				confirmText: localize("_Restore"),
				trackingId: "restore-document-to-version-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await restore(
					{
						location: convertToLocationModel(item.location),
						fileId: item.id,
						id: ensureNotNil(version.id)
					},
					globalCancellation
				);

				setIsBusy(false);
				reload();
			} catch (e) {
				alertManager.showError(localize("_FailedToRestoreTheVersion"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			item.id,
			item.location,
			openConfirmationDialog,
			reload
		]
	);

	const deleteHandler = useCallback(
		async (version: DocumentItemVersion): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: (
					<FormattedText
						text={localize("_AreYouSureYouWantToDeleteVersion")}
						thingsToReplace={[
							{ what: "{VersionNumber}", filler: version.label }
						]}
					/>
				),
				children: localize("_ThisFileWillBePermanentlyDeleted"),
				confirmText: localize("_Delete"),
				trackingId: "delete-document-version-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteVersion(
					{
						location: convertToLocationModel(item.location),
						fileId: item.id,
						id: ensureNotNil(version.id)
					},
					globalCancellation
				);

				setIsBusy(false);
				reload();
			} catch (e) {
				alertManager.showError(localize("_FailedToDeleteTheVersion"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			item.id,
			item.location,
			openConfirmationDialog,
			reload
		]
	);

	const actions = useMemo(
		(): readonly VersionAction[] => {
			return getActions({
				open: openHandler,
				download: downloadHandler,
				restore: restoreHandler,
				delete: deleteHandler
			});
		},
		[
			deleteHandler,
			downloadHandler,
			openHandler,
			restoreHandler
		]
	);

	const actionContext = useMemo(
		(): ItemlessVersionActionContext => {
			const openPossibilities: DocumentOpenPossibilities = calculate(item);

			return {
				canBeOpenedLocally: openPossibilities.canBeOpenedLocally,
				isWindows: isWindows(),
				permissions: item.permissions
			};
		},
		[item]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			const load = async (
				location: ListLocationViewModel,
				id: number,
				signal: AbortSignal
			): Promise<void> => {
				setIsBusy(true);

				try {
					const versions: readonly DocumentItemVersion[] = await get(
						{
							location: convertToLocationModel(location),
							fileId: id
						},
						signal
					);

					setVersions(versions);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();

			load(
				item.location,
				item.id,
				cancellation.signal
			);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			item,
			loadVersion
		]
	);

	return (
		<>
			<Dialog
				trackingId="sp-version-dialog"
				hasBusyIndicator={isBusy}
			>
				<Dialog.Header
					title={(
						<FormattedText
							text={localize("_VersionHistoryOf")}
							thingsToReplace={[
								{ what: "{0}", filler: item.name }
							]}
						/>
					)}
					onClose={onClose}
				/>

				<Dialog.Body>
					<p>{localize("_YouCanRestoreAnyOlderVersionBelow")}</p>
					<TableView
						trackingId="versions"
						columns={[
							{
								title: localize("_VersionNumber"),
								width: "fill",
								minWidth: 200,
								trackingId: "number"
							},
							{
								title: localize("_DateModified"),
								trackingId: "modified-on"
							},
							{
								title: localize("_ModifiedBy"),
								trackingId: "modified-by"
							}
						]}
						items={versions}
						itemKey={getItemKey}
						hasZebraPrinting
					>
						{
							(version, itemKey): ReactElement => {
								return (
									<TableView.Row
										trackingId={`${itemKey}`}
									>
										<TableView.Cell>
											<VersionName
												actions={actions}
												actionContext={actionContext}
												version={version}
												isMobile={isMobile}
												trackingId={`${itemKey}`}
											/>
										</TableView.Cell>

										<TableView.Cell>
											<DateString
												value={version.creationDate}
												format="DMY"
												timezone={userTimezone}
											/>
										</TableView.Cell>

										<TableView.Cell>{version.createdBy}</TableView.Cell>
									</TableView.Row>
								);
							}
						}
					</TableView>
				</Dialog.Body>

				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							onClick={onClose}
							text={localize("_Close")}
							trackingId="close"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{confirmationDialog}
		</>
	);
};
VersionDialog.displayName = "VersionDialog";

export type VersionNameProps = {
	version: DocumentItemVersion;
	actions: readonly VersionAction[];
	actionContext: ItemlessVersionActionContext;
	isMobile: boolean;
	trackingId: string;
};

export const VersionName = (props: VersionNameProps): ReactElement => {
	const {
		version,
		actions,
		actionContext: contextFromProp,
		isMobile,
		trackingId
	} = props;

	const context = useMemo(
		(): VersionActionContext => {
			return {
				...contextFromProp,
				version
			};
		},
		[contextFromProp, version]
	);

	const [
		executeAction,
		availableActions
	] = useMemo(
		(): [
			executeAction: VersionAction | undefined,
			availableActions: readonly VersionAction[]
		] => {
			const availableActions: readonly VersionAction[] = reduceToAvailableActions(
				actions,
				context
			);

			const executeAction: VersionAction | undefined = sortBy(
				availableActions.filter((x): boolean => isNotNil(x.executeActionIndex)),
				x => x.executeActionIndex
			)[0];

			return [
				executeAction,
				availableActions
			];
		},
		[actions, context]
	);

	const executeHandler = useMemo(
		(): (() => void) | undefined => {
			if (!executeAction) {
				return undefined;
			}

			return (): void => {
				executeAction.execute(context);
			};
		},
		[executeAction, context]
	);

	const title = useMemo(
		(): string => {
			let result: string = `${localize("_Version")} ${version.label}`;
			if (version.isCurrentVersion) {
				result += ` (${localize("_CurrentPublished")})`;
			}

			return result;
		},
		[version.isCurrentVersion, version.label]
	);

	return (
		<div className={styles.versionName}>
			<span
				className={styles.name}
				onClick={executeHandler}
				title={title}
			>
				{title}
			</span>

			<div className={styles.actionContainer}>
				<ItemActions
					availableActions={availableActions}
					actionContext={context}
					isMobile={isMobile}
					trackingId={generateTrackingId(trackingId, "__actions")}
					menuTitle={title}
				/>
			</div>
		</div>
	);
};
VersionName.displayName = "VersionName";

export type ItemActionsProps = {
	availableActions: readonly VersionAction[];
	actionContext: VersionActionContext;
	isMobile: boolean;
	trackingId: string;
	menuTitle: string;
};

export const ItemActions = (props: ItemActionsProps): ReactElement => {
	const {
		availableActions,
		actionContext,
		isMobile,
		trackingId,
		menuTitle
	} = props;

	const [areActionsOpen, setAreActionsOpen] = useState<boolean>(false);
	const actionRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const quickActions = useMemo(
		(): readonly VersionAction[] => {
			if (isMobile) {
				return [];
			}

			return sortBy(
				availableActions.filter((x): boolean => isNotNil(x.quickActionIndex)),
				x => x.quickActionIndex
			)
				.slice(0, 2);
		},
		[availableActions, isMobile]
	);

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.actions}
		>
			{quickActions.map((x): ReactElement => (
				<IconButton
					key={x.name}
					icon={ensureNotNil(x.icon)}
					kind="transparent"
					onClick={() => x.execute(actionContext)}
					trackingId={x.name}
					bubbleClick={false}
					shape="round"
				/>
			))}

			{availableActions.length > 0 && (
				<>
					<IconButton
						icon="ellipsis"
						kind="transparent"
						onClick={() => setAreActionsOpen(true)}
						trackingId="more"
						bubbleClick={false}
						ref={actionRef}
						shape="round"
					/>

					{areActionsOpen && actionRef.current && (
						<DropOverActionList
							target={actionRef.current}
							targetAlignment="bl"
							contentAlignment="tl"
							onClose={() => setAreActionsOpen(false)}
							trackingId={generateTrackingId(trackingId, "__dropover")}
							bubbleClick={false}
							menuTitle={menuTitle}
						>
							{availableActions.map((x) => (
								<Fragment key={x.name}>
									{actionComponentFactory(x, actionContext)}
								</Fragment>
							))}
						</DropOverActionList>
					)}
				</>
			)}
		</ul>
	);
};
ItemActions.displayName = "ItemActions";

type VersionActionContext = {
	version: DocumentItemVersion;
	canBeOpenedLocally: boolean;
	isWindows: boolean;
	permissions: Permissions;
};

type ItemlessVersionActionContext = Omit<VersionActionContext, "version">;

type VersionAction = Action<VersionActionContext> & {
	executeActionIndex?: number;
	quickActionIndex?: number;
};

type Handlers = {
	open: (version: DocumentItemVersion) => void;
	download: (version: DocumentItemVersion) => void;
	restore: (version: DocumentItemVersion) => void;
	delete: (version: DocumentItemVersion) => void;
};

function getActions(handlers: Handlers): readonly VersionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "open",
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (c) => c.canBeOpenedLocally && c.isWindows,
			execute: (c) => handlers.open(c.version),
			executeActionIndex: 0
		},
		{
			kind: ActionKind.Regular,
			name: "download",
			title: localize("_Download"),
			icon: "download",
			canExecute: () => true,
			execute: (c) => handlers.download(c.version),
			executeActionIndex: 1,
			quickActionIndex: 1
		},
		{
			kind: ActionKind.Regular,
			name: "restore",
			title: localize("_Restore"),
			icon: "arrow-rotate-right",
			canExecute: (c) => isNotNil(c.version.id) && c.permissions.canEdit,
			execute: (c) => handlers.restore(c.version),
			quickActionIndex: 0
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => isNotNil(c.version.id) && c.permissions.canEdit,
			execute: (c) => handlers.delete(c.version)
		}
	];
}

export function openVersionDialog(props: Omit<VersionDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<VersionDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}

function getItemKey(version: DocumentItemVersion, index: number): string {
	return isNil(version.id)
		? `NULL_${index}`
		: `${version.id}`;
}